import React, {
  SyntheticEvent,
  useMemo,
  useState,
} from 'react';
import {
  US_STATES,
  USStateType,
} from 'core/constants';
import AutoComplete from 'core/components/AutoComplete';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import {
  usePrincipalFacilityState,
} from 'views/EMR/stores/user';
import DefaultOptionContainer from 'core/components/Lookup/Generic/DefaultOptionContainer';
import compact from 'lodash/compact';
import clsx from 'clsx';
import debounce from 'lodash/debounce';

function sortOptions(options: USStateType[], state: string) {
  const stateValue = state.toLowerCase();

  return options.sort((a, b) => {
    const matchA = a.abbreviation?.toLowerCase() === stateValue ? -1 : undefined;
    const matchB = b.abbreviation?.toLowerCase() === stateValue ? 1 : undefined;

    return isNil(matchA) && isNil(matchB)
      ? a.name.localeCompare(b.name)
      : matchA ?? matchB ?? 0;
  });
}

function filterOptions(
  options: USStateType[],
  inputValue: string,
) {
  const input = inputValue?.toLowerCase();

  return notEmptyString(input)
    ? sortOptions(
      options.filter(
        (state) => state.name.toLowerCase().includes(input)
         || state.abbreviation?.toLowerCase().includes(input),
      ),
      input,
    )
    : options;
}

type Props = {
  onChange: (...event: any[]) => void;
  onBlur: () => void;
  value?: string | null;
  className?: string;
};

export default function USStateSelect({
  onChange,
  onBlur,
  value,
  className,
}: Props) {
  const selectedState = useMemo<USStateType | null>(() => US_STATES.find(
    (region) => (
      region.name === value?.trim()
      || region.abbreviation === value?.trim()
    ),
  ) ?? null, [value]);

  const principalFacilityState = usePrincipalFacilityState();

  const sortedStateOptions = useMemo(() => {
    const options = cloneDeep(US_STATES);
    return notEmptyString(principalFacilityState)
      ? sortOptions(options, principalFacilityState ?? '')
      : options;
  }, [principalFacilityState]);

  const onStateValueChange = (val: USStateType | USStateType[]) => {
    const value = (val as USStateType)?.abbreviation ?? '';
    onChange(value);
  };

  const [inputValue, setInputValue] = useState('');

  const handleInputChange = debounce(
    (event: SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
    },
    700,
  );

  const filteredOptions = useMemo(
    () => filterOptions(sortedStateOptions, inputValue),
    [sortedStateOptions, inputValue],
  );

  return (
    <AutoComplete<USStateType>
      onChange={onStateValueChange}
      onBlur={onBlur}
      value={selectedState}
      className={className}
      placeholder="State"
      id="state"
      options={filteredOptions}
      onInputChange={handleInputChange}
      filterOptions={(x) => x}
      getLabel={(region) => compact([region.abbreviation, region.name]).join(' - ')}
      isOptionEqualToValue={(a, b) => (
        a.name === (b as USStateType)?.name
        || a.abbreviation === (b as USStateType)?.abbreviation
      )}
      classes={{
        inputRoot: '!pr-2 !py-2',
        input: '!mr-4 !truncate',
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <DefaultOptionContainer
            key={key}
            {...optionProps}
            id={option.abbreviation ?? ''}
            className={clsx(optionProps.className, {
              'prefered-option': option.abbreviation?.toLowerCase() === inputValue.toLowerCase()
                || (isEmptyString(inputValue) && option.abbreviation === principalFacilityState),
            })}
          >
            <div>
              {option.abbreviation}
              {' - '}
              {option.name}
            </div>
          </DefaultOptionContainer>
        );
      }}
    />
  );
}
